import React, { useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { Zap, Plus, Pencil, Trash2, X as XIcon, Search } from "lucide-react";
import { toast } from "sonner";

/**
 * Chip/template rapidi sopra l'Esame Obiettivo: click → compila i campi giusti OD+OS.
 * Gestione CRUD (crea/modifica/elimina/cerca) dei template del medico.
 */
const APPLY_EYES = [["OU", "OD+OS"], ["OD", "solo OD"], ["OS", "solo OS"]];
const FIELD_KEYS = ["orbita", "annessi_palpebre", "congiuntiva", "cornea", "camera_anteriore", "iride", "pupille", "cristallino", "vitreo", "tono", "papilla", "macula", "vasi", "periferia_retinica", "note"];

export default function ExamPresetChips({ onApply, readOnly, eyeExcluded = {} }) {
  const [presets, setPresets] = useState([]);
  const [manageOpen, setManageOpen] = useState(false);
  const [applyEye, setApplyEye] = useState("OU");
  const [editing, setEditing] = useState(null); // {id?, label, category, fields}
  const [q, setQ] = useState("");

  const load = async () => {
    try { const { data } = await api.get("/medico/exam-presets"); setPresets(data || []); }
    catch { setPresets([]); }
  };
  useEffect(() => { load(); }, []);

  const apply = (p) => {
    const eyes = applyEye === "OU" ? ["OD", "OS"] : [applyEye];
    const targets = eyes.filter((e) => !(eyeExcluded[e] || {}).excluded);
    if (targets.length === 0) { toast.error("Occhi selezionati esclusi: impossibile applicare"); return; }
    onApply(p.fields || {}, targets);
    toast.success(`Template "${p.label}" applicato a ${targets.join(" + ")}`);
  };

  const save = async () => {
    if (!editing?.label?.trim()) { toast.error("Nome template obbligatorio"); return; }
    const fields = Object.fromEntries(Object.entries(editing.fields || {}).filter(([, v]) => (v || "").trim()));
    try {
      if (editing.id && !editing.id.startsWith("builtin_")) {
        await api.put(`/medico/exam-presets/${editing.id}`, { label: editing.label, category: editing.category, fields });
      } else {
        await api.post("/medico/exam-presets", { label: editing.label, category: editing.category, fields });
      }
      toast.success("Template salvato");
      setEditing(null);
      load();
    } catch (e) { toast.error(apiErr(e, "Errore salvataggio template")); }
  };

  const del = async (p) => {
    if (!window.confirm(`Eliminare il template "${p.label}"?`)) return;
    try { await api.delete(`/medico/exam-presets/${p.id}`); load(); toast.success("Template eliminato"); }
    catch (e) { toast.error(apiErr(e, "Errore")); }
  };

  const filtered = presets.filter((p) => !q || p.label.toLowerCase().includes(q.toLowerCase()) || (p.category || "").toLowerCase().includes(q.toLowerCase()));
  const byCat = filtered.reduce((acc, p) => { if (p.hides_builtin) return acc; (acc[p.category || "Altro"] = acc[p.category || "Altro"] || []).push(p); return acc; }, {});

  return (
    <div className="bg-slate-50 border border-[#E2E8F0] rounded-xl p-3 mb-4" data-testid="exam-presets-block">
      <div className="flex items-center gap-2 flex-wrap mb-2">
        <Zap size={14} className="text-brand-orange" />
        <span className="text-xs font-bold uppercase tracking-wider text-slate-600">Template rapidi esame obiettivo</span>
        <div className="flex gap-1 ml-1">
          {APPLY_EYES.map(([v, lbl]) => (
            <button key={v} type="button" onClick={() => setApplyEye(v)} data-testid={`preset-eye-${v.toLowerCase()}`}
              className={`px-2 py-0.5 rounded-full text-[10px] font-bold border ${applyEye === v ? "bg-brand-navy text-white border-brand-navy" : "bg-white text-slate-500 border-[#E2E8F0]"}`}>
              {lbl}
            </button>
          ))}
        </div>
        {!readOnly && (
          <button type="button" data-testid="preset-manage-open" onClick={() => setManageOpen(true)}
            className="ml-auto text-[11px] font-bold text-brand-azure hover:underline">⚙ Gestisci template</button>
        )}
      </div>
      <div className="space-y-1.5">
        {Object.entries(byCat).map(([cat, items]) => (
          <div key={cat} className="flex items-center gap-1.5 flex-wrap">
            <span className="text-[9px] uppercase tracking-wider font-bold text-slate-400 w-28 shrink-0">{cat}</span>
            {items.map((p) => (
              <button key={p.id} type="button" disabled={readOnly} data-testid={`preset-chip-${p.id}`}
                onClick={() => apply(p)} title={Object.entries(p.fields || {}).map(([k, v]) => `${k}: ${v}`).join("\n")}
                className="px-2.5 py-1 rounded-full bg-white border border-[#E2E8F0] hover:border-brand-orange hover:text-brand-orange text-[11px] font-bold text-slate-700 transition disabled:opacity-40">
                {p.label}
              </button>
            ))}
          </div>
        ))}
      </div>

      {manageOpen && (
        <div className="fixed inset-0 z-[60] bg-black/40 flex items-center justify-center p-4 overflow-y-auto" onClick={() => { setManageOpen(false); setEditing(null); }}>
          <div className="bg-white rounded-xl p-5 max-w-2xl w-full my-8" onClick={(e) => e.stopPropagation()} data-testid="preset-manage-modal">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-extrabold text-brand-navy">Gestione template esame obiettivo</h3>
              <button data-testid="preset-manage-close" onClick={() => { setManageOpen(false); setEditing(null); }}><XIcon size={18} /></button>
            </div>
            {!editing ? (
              <>
                <div className="relative mb-3">
                  <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca template…" data-testid="preset-search"
                    className="w-full pl-8 pr-3 py-2 rounded-lg border border-[#E2E8F0] text-sm" />
                </div>
                <div className="max-h-80 overflow-y-auto space-y-1.5">
                  {filtered.filter((p) => !p.hides_builtin).map((p) => (
                    <div key={p.id} className="flex items-center gap-2 border border-[#E2E8F0] rounded-lg px-3 py-2">
                      <div className="flex-1 min-w-0">
                        <span className="text-sm font-bold text-brand-navy">{p.label}</span>
                        <span className="text-[10px] text-slate-500 ml-2">{p.category}{p.builtin ? " · predefinito" : ""}</span>
                      </div>
                      <button data-testid={`preset-edit-${p.id}`} onClick={() => setEditing({ id: p.builtin ? null : p.id, label: p.label + (p.builtin ? " (copia)" : ""), category: p.category, fields: { ...(p.fields || {}) } })} className="text-slate-500 hover:text-brand-azure p-1"><Pencil size={14} /></button>
                      <button data-testid={`preset-del-${p.id}`} onClick={() => del(p)} className="text-slate-400 hover:text-red-600 p-1"><Trash2 size={14} /></button>
                    </div>
                  ))}
                </div>
                <button data-testid="preset-new" onClick={() => setEditing({ label: "", category: "Segmento anteriore", fields: {} })}
                  className="mt-3 inline-flex items-center gap-1.5 bg-brand-navy text-white text-xs font-bold px-3 py-2 rounded-lg"><Plus size={13} /> Nuovo template</button>
              </>
            ) : (
              <div className="space-y-2">
                <div className="grid grid-cols-2 gap-2">
                  <input value={editing.label} onChange={(e) => setEditing({ ...editing, label: e.target.value })} placeholder="Nome template" data-testid="preset-edit-label" className="px-3 py-2 rounded-lg border border-[#E2E8F0] text-sm" />
                  <select value={editing.category} onChange={(e) => setEditing({ ...editing, category: e.target.value })} data-testid="preset-edit-category" className="px-3 py-2 rounded-lg border border-[#E2E8F0] text-sm bg-white">
                    {["Segmento anteriore", "Cristallino", "Cornea", "Retina/Macula", "Post-chirurgico", "Glaucoma", "Urgenze", "Altro"].map((c) => <option key={c}>{c}</option>)}
                  </select>
                </div>
                <div className="max-h-72 overflow-y-auto grid grid-cols-1 md:grid-cols-2 gap-2">
                  {FIELD_KEYS.map((k) => (
                    <div key={k}>
                      <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">{k.replace(/_/g, " ")}</label>
                      <input value={(editing.fields || {})[k] || ""} onChange={(e) => setEditing({ ...editing, fields: { ...editing.fields, [k]: e.target.value } })}
                        data-testid={`preset-edit-field-${k}`} placeholder="(vuoto = non toccare)" className="w-full px-2 py-1.5 rounded border border-[#E2E8F0] text-xs" />
                    </div>
                  ))}
                </div>
                <div className="flex justify-end gap-2 pt-2">
                  <button onClick={() => setEditing(null)} className="px-4 py-2 rounded-lg text-sm font-bold text-slate-600 hover:bg-slate-100">Annulla</button>
                  <button data-testid="preset-edit-save" onClick={save} className="px-4 py-2 rounded-lg text-sm font-bold bg-brand-azure text-white">Salva template</button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
